import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ApiTransaction } from '../../../../core/models/history-api.models';
import { AuthService } from '../../../../core/services/auth.service';
import { HistoryApiService } from '../../../../core/services/history-api.service';
import { ProductApiService } from '../../../../core/services/product-api.service';

const PAGE_SIZE = 20;

interface PaymentStatusUi {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'muted';
}

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
})
export class HistoryComponent implements OnInit, OnDestroy {
  transactions: ApiTransaction[] = [];
  totalElements = 0;
  loading = true;
  loadingMore = false;
  hasMore = true;
  errorMessage = '';
  searchQuery = '';
  selected: ApiTransaction | null = null;

  private currentPage = 0;
  private userId = '';
  private productNames = new Map<string, string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly historyApi: HistoryApiService,
    private readonly productApi: ProductApiService,
  ) {}

  get completedCount(): number {
    return this.transactions.filter((item) => item.subscriptionValidated === true).length;
  }

  get pendingCount(): number {
    return this.transactions.filter((item) => item.subscriptionValidated !== true).length;
  }

  get selectedPaymentStatus(): PaymentStatusUi {
    return this.selected
      ? this.paymentStatus(this.selected)
      : { label: 'Inconnue', tone: 'muted' };
  }

  get visibleTransactions(): ApiTransaction[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.transactions;
    }

    return this.transactions.filter((item) => {
      const haystack = [
        item.reference,
        item.description,
        item.issuerId,
        item.productName,
        item.provider?.name,
        this.productName(item),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }

  ngOnInit(): void {
    this.productApi
      .list()
      .pipe(catchError(() => of([])), takeUntil(this.destroy$))
      .subscribe((catalog) => {
        this.productNames.clear();
        catalog.forEach((type) => {
          (type.products || []).forEach((product) => {
            if (product.id && product.name) {
              this.productNames.set(product.id, product.name);
            }
          });
        });
      });

    this.authService.userProfile$.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      const id = (profile?.id || this.authService.getUserId() || '').trim();
      if (!id || id === this.userId) {
        if (!id && !this.loading) {
          this.errorMessage = 'Profil utilisateur indisponible.';
        }
        return;
      }
      this.userId = id;
      this.loadFirstPage();
    });
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selected) {
      this.closeDetail();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.loading || this.loadingMore || !this.hasMore || this.selected) {
      return;
    }

    const doc = document.documentElement;
    const nearBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 200;
    if (nearBottom) {
      this.loadMore();
    }
  }

  reload(): void {
    this.loadFirstPage();
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value || '';
  }

  openDetail(transaction: ApiTransaction): void {
    this.selected = transaction;
    this.lockPageScroll();
  }

  closeDetail(): void {
    this.selected = null;
    this.unlockPageScroll();
  }

  isComplete(transaction: ApiTransaction): boolean {
    return transaction.subscriptionValidated === true;
  }

  statusLabel(transaction: ApiTransaction): string {
    return this.isComplete(transaction) ? 'Complète' : 'Incomplète';
  }

  statusTone(transaction: ApiTransaction): 'ok' | 'warn' {
    return this.isComplete(transaction) ? 'ok' : 'warn';
  }

  typeIcon(transaction: ApiTransaction): string {
    switch ((transaction.description || '').trim()) {
      case 'Achat':
        return 'shopping_cart';
      case 'Virement':
        return 'swap_horiz';
      case 'Paiement':
        return 'payment';
      case 'Dépôt':
        return 'attach_money';
      default:
        return 'payment';
    }
  }

  typeClass(transaction: ApiTransaction): string {
    switch ((transaction.description || '').trim().toUpperCase()) {
      case 'ACHAT':
        return 'type-achat';
      case 'VIREMENT':
        return 'type-virement';
      case 'PAIEMENT':
        return 'type-paiement';
      case 'DEPOT':
      case 'DÉPÔT':
        return 'type-depot';
      default:
        return 'type-default';
    }
  }

  sentenceCase(value: string | null | undefined): string {
    const text = (value || '').trim();
    if (!text) {
      return '-';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  providerName(transaction: ApiTransaction): string {
    return (transaction.provider?.name || '-').toUpperCase();
  }

  productName(transaction: ApiTransaction): string {
    const fromCatalog = this.productNames.get(transaction.productId || '');
    return fromCatalog || transaction.productName || '-';
  }

  formatAmount(transaction: ApiTransaction): string {
    const amount = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(
      transaction.amount || 0,
    );
    const code = (transaction.currency?.shortCode || '').toUpperCase();
    return code ? `${amount} ${code}` : amount;
  }

  formatCardDate(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return '-';
    }
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDetailDate(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) {
      return '-';
    }
    const day = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${day} à ${time}`;
  }

  issuerLabel(transaction: ApiTransaction): string {
    switch ((transaction.provider?.payType || '').toUpperCase()) {
      case 'MOBILE':
        return 'Téléphone';
      case 'CARD':
        return 'Numéro de compte';
      default:
        return 'Compte';
    }
  }

  contractOrProjectLabel(transaction: ApiTransaction): string {
    return transaction.contractNumber ? 'Numéro de police' : 'Numéro du devis';
  }

  contractOrProjectValue(transaction: ApiTransaction): string {
    return transaction.contractNumber || transaction.projectNumber || '-';
  }

  operationStatusLabel(transaction: ApiTransaction): string {
    return `Validation ${transaction.description || 'opération'} échoué(e)`;
  }

  private paymentStatus(transaction: ApiTransaction): PaymentStatusUi {
    const latest = this.latestStatus(transaction);
    const code = (latest?.status?.label || latest?.status?.code || '').toUpperCase();

    switch (code) {
      case 'PENDING':
        return { label: 'Transaction en attente...', tone: 'warn' };
      case 'SUCCESSFULL':
      case 'SUCCESSFUL':
      case 'SUCCESS':
        return { label: 'Transaction réussie', tone: 'ok' };
      case 'FAILED':
        return { label: 'Transaction échouée', tone: 'danger' };
      default:
        return { label: latest?.status?.label || 'Inconnue', tone: 'muted' };
    }
  }

  private latestStatus(transaction: ApiTransaction) {
    const statuses = [...(transaction.transactionStatus || [])].sort((a, b) => {
      const aTime = this.parseDate(a.createdAt)?.getTime() || 0;
      const bTime = this.parseDate(b.createdAt)?.getTime() || 0;
      return bTime - aTime;
    });
    return statuses[0] || null;
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private lockPageScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockPageScroll(): void {
    document.body.style.overflow = '';
  }

  private loadFirstPage(): void {
    if (!this.userId) {
      this.loading = false;
      this.errorMessage = 'Profil utilisateur indisponible.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.hasMore = true;
    this.currentPage = 0;
    this.transactions = [];
    this.closeDetail();

    this.historyApi
      .fetchByIssuerPaged(this.userId, 0, PAGE_SIZE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.transactions = page.transactions;
          this.totalElements = page.totalElements;
          this.hasMore = !page.isLast;
          this.currentPage = 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Erreur de chargement';
        },
      });
  }

  private loadMore(): void {
    if (!this.userId || this.loadingMore || !this.hasMore) {
      return;
    }

    this.loadingMore = true;
    const nextPage = this.currentPage + 1;

    this.historyApi
      .fetchByIssuerPaged(this.userId, nextPage, PAGE_SIZE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.transactions = [...this.transactions, ...page.transactions];
          this.totalElements = page.totalElements;
          this.hasMore = !page.isLast;
          this.currentPage = nextPage;
          this.loadingMore = false;
        },
        error: () => {
          this.loadingMore = false;
        },
      });
  }
}
